import React from 'react';
import { MapPin } from 'lucide-react';

// Helper: check if a color is dark
function isColorDark(hex) {
  if (!hex || hex.startsWith('rgba')) return hex?.includes('0.') ? true : false;
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) || 0;
  const g = parseInt(h.substring(2, 4), 16) || 0;
  const b = parseInt(h.substring(4, 6), 16) || 0;
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

// Helper: lighten a hex color
function lighten(hex, amount) {
  if (!hex || !hex.startsWith('#')) return hex;
  const h = hex.replace('#', '');
  const r = Math.min(255, parseInt(h.substring(0, 2), 16) + Math.round(255 * amount));
  const g = Math.min(255, parseInt(h.substring(2, 4), 16) + Math.round(255 * amount));
  const b = Math.min(255, parseInt(h.substring(4, 6), 16) + Math.round(255 * amount));
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
}

// Helper: get contrast text color for a background
function getContrastText(hex) {
  return isColorDark(hex) ? '#ffffff' : '#1e293b';
}

// Seat rendering constants
const SEAT_DIAMETER = 8;   // you render 8x8
const SEAT_BORDER = 1;     // you render 1px border
const STROKE_PAD = 2;      // table border is 2px

const SEAT_GAP_ROUND = 5;      // round seats are 5px outside table edge
const SEAT_GAP_DEFAULT = 8;    // all other shapes use 8px outside

const SEAT_PAD_ROUND = Math.ceil(SEAT_GAP_ROUND + SEAT_DIAMETER / 2 + SEAT_BORDER);      // 10
const SEAT_PAD_DEFAULT = Math.ceil(SEAT_GAP_DEFAULT + SEAT_DIAMETER / 2 + SEAT_BORDER);  // 13

function getSeatPad(shape) {
  const isRound = shape === "round" || shape === "circle" || !shape;
  return isRound ? SEAT_PAD_ROUND : SEAT_PAD_DEFAULT;
}

// Complete shape configs matching the organizer view
const SHAPE_CONFIGS = {
  round: { width: 80, height: 80, borderRadius: '50%' },
  rectangle: { width: 120, height: 60, borderRadius: '8px' },
  square: { width: 80, height: 80, borderRadius: '8px' },
  oval: { width: 120, height: 80, borderRadius: '50%' },
  lshape: { width: 120, height: 120, borderRadius: '8px', isCustomSvg: true },
  ushape: { width: 140, height: 100, borderRadius: '8px', isCustomSvg: true },
  sshape: { width: 240, height: 120, borderRadius: '0', isCustomSvg: true },
  halfround: { width: 200, height: 100, borderRadius: '0', isCustomSvg: true },
  serpentine3: { width: 360, height: 120, borderRadius: '0', isCustomSvg: true },
  serpentine4: { width: 480, height: 120, borderRadius: '0', isCustomSvg: true },
  serpentine5: { width: 600, height: 120, borderRadius: '0', isCustomSvg: true },
  serpentine6: { width: 720, height: 120, borderRadius: '0', isCustomSvg: true },
  serpentine7: { width: 840, height: 120, borderRadius: '0', isCustomSvg: true },
  crescent: { width: 140, height: 80, borderRadius: '40%', isCustomSvg: true },
};

// Custom piece default sizes
const PIECE_CONFIGS = {
  piece_rectangle: { width: 100, height: 50 },
  piece_square: { width: 60, height: 60 },
  piece_circle: { width: 60, height: 60, isCircle: true },
  piece_wave: { width: 120, height: 60, isArc: true, svgPath: 'M422.7,276.9c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6Z', viewBox: '270 260 320 160' },
};

// Venue element configurations
const VENUE_ELEMENT_CONFIGS = {
  stage: { label: 'STAGE', defaultWidth: 200, defaultHeight: 60 },
  dance_floor: { label: 'DANCE FLOOR', defaultWidth: 150, defaultHeight: 150 },
  bar: { label: 'BAR', defaultWidth: 120, defaultHeight: 30 },
  dj_booth: { label: 'DJ', defaultWidth: 50, defaultHeight: 50 },
  photo_booth: { label: 'PHOTO BOOTH', defaultWidth: 60, defaultHeight: 60 },
  entrance: { label: 'ENTRANCE', defaultWidth: 80, defaultHeight: 30 },
  cocktail_table: { label: 'COCKTAIL', defaultWidth: 30, defaultHeight: 30, isCircle: true },
  restrooms: { label: 'WC', defaultWidth: 50, defaultHeight: 50 },
};

// Calculate seat positions for different table shapes
function calculateSeatPositions(shape, width, height, seats) {
  const positions = [];
  const seatSize = SEAT_DIAMETER;
  const isRound = shape === "round" || shape === "circle" || !shape;
  const gap = isRound ? SEAT_GAP_ROUND : SEAT_GAP_DEFAULT;
  
  switch (shape) {
    case 'round':
    case 'circle': {
      const radius = (width / 2) + gap;
      for (let i = 0; i < seats; i++) {
        const angle = (2 * Math.PI * i) / seats - Math.PI / 2;
        positions.push({ x: width / 2 + Math.cos(angle) * radius - seatSize / 2, y: height / 2 + Math.sin(angle) * radius - seatSize / 2 });
      }
      break;
    }
    case 'oval': {
      const rx = (width / 2) + gap;
      const ry = (height / 2) + gap;
      for (let i = 0; i < seats; i++) {
        const angle = (2 * Math.PI * i) / seats - Math.PI / 2;
        positions.push({ x: width / 2 + Math.cos(angle) * rx - seatSize / 2, y: height / 2 + Math.sin(angle) * ry - seatSize / 2 });
      }
      break;
    }
    case 'rectangle': {
      const seatsPerSide = Math.ceil(seats / 2);
      const spacing = (width - 20) / (seatsPerSide + 1);
      for (let i = 0; i < Math.min(seatsPerSide, seats); i++) {
        positions.push({ x: 10 + spacing * (i + 1) - seatSize / 2, y: -gap - seatSize / 2 });
      }
      for (let i = 0; i < Math.min(seatsPerSide, seats - seatsPerSide); i++) {
        positions.push({ x: 10 + spacing * (i + 1) - seatSize / 2, y: height + gap - seatSize / 2 });
      }
      break;
    }
    case 'square': {
      const seatsPerSide = Math.ceil(seats / 4);
      const sideSeats = [Math.min(seatsPerSide, seats), Math.min(seatsPerSide, Math.max(0, seats - seatsPerSide)), Math.min(seatsPerSide, Math.max(0, seats - seatsPerSide * 2)), Math.min(seatsPerSide, Math.max(0, seats - seatsPerSide * 3))];
      for (let i = 0; i < sideSeats[0]; i++) { const spacing = width / (sideSeats[0] + 1); positions.push({ x: spacing * (i + 1) - seatSize / 2, y: -gap - seatSize / 2 }); }
      for (let i = 0; i < sideSeats[1]; i++) { const spacing = height / (sideSeats[1] + 1); positions.push({ x: width + gap - seatSize / 2, y: spacing * (i + 1) - seatSize / 2 }); }
      for (let i = 0; i < sideSeats[2]; i++) { const spacing = width / (sideSeats[2] + 1); positions.push({ x: spacing * (i + 1) - seatSize / 2, y: height + gap - seatSize / 2 }); }
      for (let i = 0; i < sideSeats[3]; i++) { const spacing = height / (sideSeats[3] + 1); positions.push({ x: -gap - seatSize / 2, y: spacing * (i + 1) - seatSize / 2 }); }
      break;
    }
    case 'lshape': {
      const verticalSeats = Math.ceil(seats * 0.5);
      const horizontalSeats = seats - verticalSeats;
      for (let i = 0; i < verticalSeats; i++) { const spacing = height / (verticalSeats + 1); positions.push({ x: -gap - seatSize / 2, y: spacing * (i + 1) - seatSize / 2 }); }
      for (let i = 0; i < horizontalSeats; i++) { const spacing = width / (horizontalSeats + 1); positions.push({ x: spacing * (i + 1) - seatSize / 2, y: height + gap - seatSize / 2 }); }
      break;
    }
    case 'ushape': {
      const sideSeats = Math.floor(seats / 3);
      const bottomSeats = seats - sideSeats * 2;
      for (let i = 0; i < sideSeats; i++) { const spacing = (height - 20) / (sideSeats + 1); positions.push({ x: -gap - seatSize / 2, y: 10 + spacing * (i + 1) - seatSize / 2 }); }
      for (let i = 0; i < bottomSeats; i++) { const spacing = (width - 40) / (bottomSeats + 1); positions.push({ x: 20 + spacing * (i + 1) - seatSize / 2, y: height + gap - seatSize / 2 }); }
      for (let i = 0; i < sideSeats; i++) { const spacing = (height - 20) / (sideSeats + 1); positions.push({ x: width + gap - seatSize / 2, y: 10 + spacing * (i + 1) - seatSize / 2 }); }
      break;
    }
    case 'sshape': {
      const viewBoxWidth = 510, viewBoxHeight = 290, viewBoxX = 380, viewBoxY = 290;
      const svgPositions = [{ x: 408.4, y: 351.4 }, { x: 480.7, y: 323.5 }, { x: 561.4, y: 323.9 }, { x: 634.8, y: 351.8 }, { x: 492.8, y: 446.6 }, { x: 552.3, y: 447.6 }, { x: 627.1, y: 499.6 }, { x: 699.4, y: 527.5 }, { x: 780.1, y: 527.1 }, { x: 853.5, y: 499.2 }, { x: 711.5, y: 404.4 }, { x: 771.0, y: 403.4 }];
      svgPositions.slice(0, seats).forEach(p => { positions.push({ x: ((p.x - viewBoxX) / viewBoxWidth) * width - seatSize / 2, y: ((p.y - viewBoxY) / viewBoxHeight) * height - seatSize / 2 }); });
      break;
    }
    case 'halfround': {
      const viewBoxWidth = 450, viewBoxHeight = 220, viewBoxX = 200, viewBoxY = 310;
      const svgPositions = [{ x: 215.3, y: 486.3 }, { x: 248.0, y: 414.9 }, { x: 305.2, y: 358.0 }, { x: 376.3, y: 327.2 }, { x: 341.3, y: 496.7 }, { x: 383.0, y: 454.2 }, { x: 475.9, y: 328.0 }, { x: 546.6, y: 359.8 }, { x: 603.0, y: 417.5 }, { x: 634.7, y: 489.4 }, { x: 467.5, y: 454.9 }, { x: 508.6, y: 498.0 }];
      svgPositions.slice(0, seats).forEach(p => { positions.push({ x: ((p.x - viewBoxX) / viewBoxWidth) * width - seatSize / 2, y: ((p.y - viewBoxY) / viewBoxHeight) * height - seatSize / 2 }); });
      break;
    }
    case 'serpentine3': {
      // SVG viewBox="70 260 710 290" - seats positioned along the 3-arc serpentine curve
      const viewBoxWidth = 710, viewBoxHeight = 290, viewBoxX = 70, viewBoxY = 260;
      const svgPositions = [
        // Arc 1 (top-left)
        { x: 92.8, y: 351.6 }, { x: 165.1, y: 323.7 }, { x: 245.8, y: 324.1 }, { x: 319.2, y: 352.0 },
        // Arc 1-2 junction (bottom)
        { x: 177.2, y: 446.8 }, { x: 236.7, y: 447.8 },
        // Arc 2 (bottom-middle)
        { x: 311.5, y: 499.8 }, { x: 383.8, y: 527.7 }, { x: 464.5, y: 527.3 }, { x: 537.9, y: 499.4 },
        // Arc 2-3 junction (top)
        { x: 395.9, y: 404.6 }, { x: 455.4, y: 403.6 },
        // Arc 3 (top-right)
        { x: 530.7, y: 351.5 }, { x: 603.0, y: 323.6 }, { x: 683.7, y: 324.0 }, { x: 757.1, y: 351.8 },
        // Arc 3 ends (bottom)
        { x: 615.1, y: 446.7 }, { x: 674.6, y: 447.7 }
      ];
      svgPositions.slice(0, seats).forEach(p => { positions.push({ x: ((p.x - viewBoxX) / viewBoxWidth) * width - seatSize / 2, y: ((p.y - viewBoxY) / viewBoxHeight) * height - seatSize / 2 }); });
      break;
    }
    case 'serpentine4': {
      // SVG viewBox="70 260 925 290" - seats positioned along the 4-arc serpentine curve
      const viewBoxWidth = 925, viewBoxHeight = 290, viewBoxX = 70, viewBoxY = 260;
      const svgPositions = [
        // Arc 1 (top-left)
        { x: 92.8, y: 351.6 }, { x: 165.1, y: 323.7 }, { x: 245.8, y: 324.1 }, { x: 319.2, y: 352.0 },
        // Arc 1-2 junction
        { x: 177.2, y: 446.8 }, { x: 236.7, y: 447.8 },
        // Arc 2 (bottom)
        { x: 311.5, y: 499.8 }, { x: 383.8, y: 527.7 }, { x: 464.5, y: 527.3 }, { x: 537.9, y: 499.4 },
        // Arc 2-3 junction
        { x: 395.9, y: 404.6 }, { x: 455.4, y: 403.6 },
        // Arc 3 (top)
        { x: 530.7, y: 351.5 }, { x: 603.0, y: 323.6 }, { x: 683.7, y: 324.0 }, { x: 757.1, y: 351.8 },
        // Arc 3-4 junction
        { x: 615.1, y: 446.7 }, { x: 674.6, y: 447.7 },
        // Arc 4 (bottom-right)
        { x: 749.5, y: 499.8 }, { x: 821.8, y: 527.7 }, { x: 902.5, y: 527.3 }, { x: 975.9, y: 499.4 },
        // Arc 4 ends
        { x: 833.9, y: 404.6 }, { x: 893.4, y: 403.6 }
      ];
      svgPositions.slice(0, seats).forEach(p => { positions.push({ x: ((p.x - viewBoxX) / viewBoxWidth) * width - seatSize / 2, y: ((p.y - viewBoxY) / viewBoxHeight) * height - seatSize / 2 }); });
      break;
    }
    case 'serpentine5': {
      // SVG viewBox="70 260 1145 290" - seats positioned along the 5-arc serpentine curve
      const viewBoxWidth = 1145, viewBoxHeight = 290, viewBoxX = 70, viewBoxY = 260;
      const svgPositions = [
        // Arc 1
        { x: 92.8, y: 351.6 }, { x: 165.1, y: 323.7 }, { x: 245.8, y: 324.1 }, { x: 319.2, y: 352.0 },
        { x: 177.2, y: 446.8 }, { x: 236.7, y: 447.8 },
        // Arc 2
        { x: 311.5, y: 499.8 }, { x: 383.8, y: 527.7 }, { x: 464.5, y: 527.3 }, { x: 537.9, y: 499.4 },
        { x: 395.9, y: 404.6 }, { x: 455.4, y: 403.6 },
        // Arc 3
        { x: 530.7, y: 351.5 }, { x: 603.0, y: 323.6 }, { x: 683.7, y: 324.0 }, { x: 757.1, y: 351.8 },
        { x: 615.1, y: 446.7 }, { x: 674.6, y: 447.7 },
        // Arc 4
        { x: 749.5, y: 499.8 }, { x: 821.8, y: 527.7 }, { x: 902.5, y: 527.3 }, { x: 975.9, y: 499.4 },
        { x: 833.9, y: 404.6 }, { x: 893.4, y: 403.6 },
        // Arc 5
        { x: 968.7, y: 351.5 }, { x: 1041.0, y: 323.6 }, { x: 1121.7, y: 324.0 }, { x: 1195.1, y: 351.8 },
        { x: 1053.1, y: 446.7 }, { x: 1112.6, y: 447.7 }
      ];
      svgPositions.slice(0, seats).forEach(p => { positions.push({ x: ((p.x - viewBoxX) / viewBoxWidth) * width - seatSize / 2, y: ((p.y - viewBoxY) / viewBoxHeight) * height - seatSize / 2 }); });
      break;
    }
    case 'serpentine6': {
      // SVG viewBox="70 260 1365 290" - seats positioned along the 6-arc serpentine curve
      const viewBoxWidth = 1365, viewBoxHeight = 290, viewBoxX = 70, viewBoxY = 260;
      const svgPositions = [
        // Arc 1
        { x: 92.8, y: 351.6 }, { x: 165.1, y: 323.7 }, { x: 245.8, y: 324.1 }, { x: 319.2, y: 352.0 },
        { x: 177.2, y: 446.8 }, { x: 236.7, y: 447.8 },
        // Arc 2
        { x: 311.5, y: 499.8 }, { x: 383.8, y: 527.7 }, { x: 464.5, y: 527.3 }, { x: 537.9, y: 499.4 },
        { x: 395.9, y: 404.6 }, { x: 455.4, y: 403.6 },
        // Arc 3
        { x: 530.7, y: 351.5 }, { x: 603.0, y: 323.6 }, { x: 683.7, y: 324.0 }, { x: 757.1, y: 351.8 },
        { x: 615.1, y: 446.7 }, { x: 674.6, y: 447.7 },
        // Arc 4
        { x: 749.5, y: 499.8 }, { x: 821.8, y: 527.7 }, { x: 902.5, y: 527.3 }, { x: 975.9, y: 499.4 },
        { x: 833.9, y: 404.6 }, { x: 893.4, y: 403.6 },
        // Arc 5
        { x: 968.7, y: 351.5 }, { x: 1041.0, y: 323.6 }, { x: 1121.7, y: 324.0 }, { x: 1195.1, y: 351.8 },
        { x: 1053.1, y: 446.7 }, { x: 1112.6, y: 447.7 },
        // Arc 6
        { x: 1187.5, y: 499.8 }, { x: 1259.8, y: 527.7 }, { x: 1340.5, y: 527.3 }, { x: 1413.9, y: 499.4 },
        { x: 1271.9, y: 404.6 }, { x: 1331.4, y: 403.6 }
      ];
      svgPositions.slice(0, seats).forEach(p => { positions.push({ x: ((p.x - viewBoxX) / viewBoxWidth) * width - seatSize / 2, y: ((p.y - viewBoxY) / viewBoxHeight) * height - seatSize / 2 }); });
      break;
    }
    case 'serpentine7': {
      // SVG viewBox="70 260 1585 290" - seats positioned along the 7-arc serpentine curve
      const viewBoxWidth = 1585, viewBoxHeight = 290, viewBoxX = 70, viewBoxY = 260;
      const svgPositions = [
        // Arc 1
        { x: 92.8, y: 351.6 }, { x: 165.1, y: 323.7 }, { x: 245.8, y: 324.1 }, { x: 319.2, y: 352.0 },
        { x: 177.2, y: 446.8 }, { x: 236.7, y: 447.8 },
        // Arc 2
        { x: 311.5, y: 499.8 }, { x: 383.8, y: 527.7 }, { x: 464.5, y: 527.3 }, { x: 537.9, y: 499.4 },
        { x: 395.9, y: 404.6 }, { x: 455.4, y: 403.6 },
        // Arc 3
        { x: 530.7, y: 351.5 }, { x: 603.0, y: 323.6 }, { x: 683.7, y: 324.0 }, { x: 757.1, y: 351.8 },
        { x: 615.1, y: 446.7 }, { x: 674.6, y: 447.7 },
        // Arc 4
        { x: 749.5, y: 499.8 }, { x: 821.8, y: 527.7 }, { x: 902.5, y: 527.3 }, { x: 975.9, y: 499.4 },
        { x: 833.9, y: 404.6 }, { x: 893.4, y: 403.6 },
        // Arc 5
        { x: 968.7, y: 351.5 }, { x: 1041.0, y: 323.6 }, { x: 1121.7, y: 324.0 }, { x: 1195.1, y: 351.8 },
        { x: 1053.1, y: 446.7 }, { x: 1112.6, y: 447.7 },
        // Arc 6
        { x: 1187.5, y: 499.8 }, { x: 1259.8, y: 527.7 }, { x: 1340.5, y: 527.3 }, { x: 1413.9, y: 499.4 },
        { x: 1271.9, y: 404.6 }, { x: 1331.4, y: 403.6 },
        // Arc 7
        { x: 1406.7, y: 351.5 }, { x: 1479.0, y: 323.6 }, { x: 1559.7, y: 324.0 }, { x: 1633.1, y: 351.8 },
        { x: 1491.1, y: 446.7 }, { x: 1550.6, y: 447.7 }
      ];
      svgPositions.slice(0, seats).forEach(p => { positions.push({ x: ((p.x - viewBoxX) / viewBoxWidth) * width - seatSize / 2, y: ((p.y - viewBoxY) / viewBoxHeight) * height - seatSize / 2 }); });
      break;
    }
    case 'crescent': {
      const radius = (width / 2) - 10;
      for (let i = 0; i < seats; i++) {
        const angle = (Math.PI * 0.7 * i) / (seats - 1 || 1) + Math.PI * 0.15;
        positions.push({ x: width / 2 + Math.cos(angle) * radius - seatSize / 2, y: height / 2 + Math.sin(angle) * (radius * 0.6) - seatSize / 2 });
      }
      break;
    }
    default: {
      const r = (Math.min(width, height) / 2) + gap;
      for (let i = 0; i < seats; i++) {
        const angle = (2 * Math.PI * i) / seats - Math.PI / 2;
        positions.push({ x: width / 2 + Math.cos(angle) * r - seatSize / 2, y: height / 2 + Math.sin(angle) * r - seatSize / 2 });
      }
    }
  }
  return positions;
}

// Render SVG shapes for complex table types
function renderTableSVG(shape, width, height, strokeColor, fillColor) {
  const svgStyle = { position: 'absolute', inset: 0, width: '100%', height: '100%' };
  
  switch (shape) {
    case 'lshape':
      return <svg viewBox={`0 0 ${width} ${height}`} style={svgStyle} preserveAspectRatio="none"><path d={`M 0 0 L ${width * 0.25} 0 L ${width * 0.25} ${height * 0.75} L ${width} ${height * 0.75} L ${width} ${height} L 0 ${height} Z`} fill={fillColor} stroke={strokeColor} strokeWidth="6"/></svg>;
    case 'ushape':
      return <svg viewBox={`0 0 ${width} ${height}`} style={svgStyle} preserveAspectRatio="none"><path d={`M 0 0 L ${width * 0.25} 0 L ${width * 0.25} ${height * 0.6} L ${width * 0.75} ${height * 0.6} L ${width * 0.75} 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`} fill={fillColor} stroke={strokeColor} strokeWidth="6"/></svg>;
    case 'sshape':
      return <svg viewBox="380 290 510 290" style={svgStyle} preserveAspectRatio="none"><path d="M530.7,335.9c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="2"/><path d="M663.4,393.3c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6 L882.2,457.9c-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8 Z" fill={fillColor} stroke={strokeColor} strokeWidth="2"/></svg>;
    case 'halfround':
      return <svg viewBox="200 310 450 230" style={svgStyle} preserveAspectRatio="xMidYMid meet"><path d="M278.2,401.9c6.8-6.7,13.3-13.7,20.7-19.6,5.2-4.4,10.7-8.3,16.4-11.9,4.1-2.9,8.6-5.1,12.8-7.8,2.1-1.3,4.2-2.2,6.3-3.3,7.8-4.6,16-7.7,24.4-10.8,7-2.4,14-4.8,21.5-6.3,14.6-3.7,30.2-5,45.2-5.5.3.9.5,1.6.5,2.6-.3,11.7-.3,23.4-.4,35.2-.4,18-.2,35.8-.6,53.9-9.2.5-18.1,1.3-27,4-5.2,1.7-10.3,3-15.2,5.4-4.4,1.8-8.5,4-12.6,6.3-7,4.8-14,9.2-20.1,15.3-5.5,5.1-10.4,10.7-14.6,17-7.6,10.1-12.7,22.3-16.3,34.3-1.7,6.3-2.7,12.6-3.5,19.1-.2,2.3,0,4.5-.3,6.8-1.9.2-3.7,0-5.7,0-22.1,0-44.1-.3-66-.2-6.6-.3-13.3,0-19.9-.3.2-3.4.4-6.9.5-10.3,0-4.8,1.1-9.8,1.6-14.6.4-2.6,1.1-5.2,1.3-7.8.5-5.3,2.1-10.4,3.3-15.6,2-8,5.1-15.8,8-23.5,3.1-8.7,7.6-16.4,11.8-24.5,3.9-6.2,7.7-12.4,11.9-18.4,4.9-6.7,10.4-13.1,16.1-19.2h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M426.6,336.8c15.1.7,30.6,2.2,45.1,6.1,7.4,1.5,14.4,4,21.4,6.6,8.3,3.3,16.5,6.5,24.3,11.2,2.1,1.1,4.2,2,6.3,3.4,4.2,2.8,8.6,5,12.7,8,5.6,3.7,11.1,7.6,16.2,12.1,7.3,6,13.8,13.1,20.5,19.9h0c5.5,6.3,11,12.7,15.8,19.5,4.1,6.1,7.8,12.4,11.6,18.6,4.1,8.2,8.5,16,11.4,24.7,2.9,7.7,5.8,15.6,7.7,23.6,1.2,5.2,2.6,10.3,3.1,15.6.2,2.6.9,5.2,1.2,7.8.4,4.8,1.4,9.8,1.4,14.6,0,3.5.2,6.9.4,10.3-6.6.2-13.3-.2-19.9,0-21.9-.4-44-.4-66-.7-1.9,0-3.7.2-5.7,0-.3-2.3-.1-4.5-.2-6.8-.7-6.4-1.7-12.8-3.3-19.1-3.5-12-8.3-24.4-15.8-34.5-4.1-6.4-8.9-12-14.3-17.2-6-6.2-12.9-10.6-19.8-15.5-4.1-2.3-8.2-4.6-12.5-6.4-4.9-2.5-9.9-3.8-15.1-5.6-8.8-2.8-17.7-3.7-26.9-4.3" fill={fillColor} stroke={strokeColor} strokeWidth="6"/></svg>;
    case 'serpentine3':
      return <svg viewBox="70 260 710 290" style={svgStyle} preserveAspectRatio="none"><path d="M215.1,336.1c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M347.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L566.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M653,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/></svg>;
    case 'serpentine4':
      return <svg viewBox="70 260 925 290" style={svgStyle} preserveAspectRatio="none"><path d="M215.1,336.1c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M347.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L566.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M653,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M785.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L1004.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/></svg>;
    case 'serpentine5':
      return <svg viewBox="70 260 1145 290" style={svgStyle} preserveAspectRatio="none"><path d="M215.1,336.1c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M347.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L566.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M653,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M785.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L1004.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M1091,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/></svg>;
    case 'serpentine6':
      return <svg viewBox="70 260 1365 290" style={svgStyle} preserveAspectRatio="none"><path d="M215.1,336.1c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M347.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L566.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M653,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M785.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L1004.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M1091,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M1223.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L1442.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/></svg>;
    case 'serpentine7':
      return <svg viewBox="70 260 1585 290" style={svgStyle} preserveAspectRatio="none"><path d="M215.1,336.1c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M347.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L566.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M653,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M785.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L1004.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M1091,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M1223.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L1442.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/><path d="M1529,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="6"/></svg>;
    case 'crescent':
      return <svg viewBox={`0 0 ${width} ${height}`} style={svgStyle}><path d={`M ${width * 0.1} ${height * 0.8} Q ${width * 0.1} ${height * 0.1} ${width * 0.5} ${height * 0.1} Q ${width * 0.9} ${height * 0.1} ${width * 0.9} ${height * 0.8} Q ${width * 0.7} ${height * 0.5} ${width * 0.5} ${height * 0.5} Q ${width * 0.3} ${height * 0.5} ${width * 0.1} ${height * 0.8} Z`} fill={fillColor} stroke={strokeColor} strokeWidth="2"/></svg>;
    default:
      return null;
  }
}

// Render a custom piece shape
function CustomPieceShape({ piece, scale, primaryColor, isHighlighted, styles = {} }) {
  const config = PIECE_CONFIGS[piece.piece_type] || PIECE_CONFIGS.piece_rectangle;
  const pieceWidth = (piece.width || config.width) * scale;
  const pieceHeight = (piece.height || config.height) * scale;
  // Use style panel colors, then piece-specific colors, then defaults (cloud dancer)
  const strokeColor = piece.stroke_color || styles.custom_piece_stroke_color || (isHighlighted ? primaryColor : '#1e40af');
  const fillColor = piece.fill_color || styles.custom_piece_fill_color || '#f0eee9';
  
  if (config.isArc && config.svgPath) {
    // Arc shape using original SVG path
    return (
      <svg 
        width={pieceWidth} 
        height={pieceHeight} 
        viewBox={config.viewBox} 
        preserveAspectRatio="none" 
        style={{ transform: piece.rotation ? `rotate(${piece.rotation}deg)` : 'none' }}
      >
        <path 
          d={config.svgPath} 
          fill={fillColor} 
          stroke={strokeColor} 
          strokeWidth="6"
        />
      </svg>
    );
  }
  if (config.isCircle) {
    return <div style={{ width: pieceWidth, height: pieceHeight, borderRadius: '50%', border: `2px solid ${strokeColor}`, backgroundColor: fillColor, transform: piece.rotation ? `rotate(${piece.rotation}deg)` : 'none' }}/>;
  }
  return <div style={{ width: pieceWidth, height: pieceHeight, borderRadius: '4px', border: `2px solid ${strokeColor}`, backgroundColor: fillColor, transform: piece.rotation ? `rotate(${piece.rotation}deg)` : 'none' }}/>;
}

export default function PublicFloorPlan({ tables, venueElements = [], customPieces = [], textBoxes = [], highlightedTableId, themeColors = {}, compact = false, fullScreen = false, containerSize, floorPlanStyle = {} }) {
  // containerSize is passed from parent for fullScreen mode
  
  const tc = themeColors;
  const isDarkTheme = tc.background && isColorDark(tc.background);

  // Derive floor plan colors from theme when no custom floor plan style is set
  const themeDefaults = tc.primary ? {
    table_fill_color: isDarkTheme ? lighten(tc.background, 0.12) : '#ffffff',
    table_stroke_color: tc.border || (isDarkTheme ? 'rgba(255,255,255,0.15)' : '#d1d5db'),
    highlighted_table_color: tc.primary,
    venue_element_color: isDarkTheme ? lighten(tc.background, 0.08) : (tc.border || '#e5e7eb'),
    venue_element_stroke: isDarkTheme ? 'rgba(255,255,255,0.1)' : (tc.textMuted || '#9ca3af'),
    custom_piece_fill_color: isDarkTheme ? lighten(tc.background, 0.06) : '#f0eee9',
    custom_piece_stroke_color: tc.primary,
    seat_color: tc.accent || tc.primary,
    background_color: tc.background || '#f8fafc',
    table_name_font_color: isDarkTheme ? tc.text || '#ffffff' : tc.text || '#1e293b',
    highlighted_font_color: isDarkTheme ? '#ffffff' : getContrastText(tc.primary),
  } : {};

  // Default floor plan style values
  const DEFAULTS = {
    table_fill_color: '#ffffff',
    table_stroke_color: '#d1d5db',
    table_stroke_width: 2,
    table_name_font_size: 14,
    highlighted_table_color: '#a3b6a0',
    venue_element_color: '#e5e7eb',
    venue_element_stroke: '#9ca3af',
    custom_piece_fill_color: '#f0eee9',
    custom_piece_stroke_color: '#1e40af',
    seat_color: '#9ca3af',
    background_color: '#f8fafc',
  };

  // Detect if user has explicitly customized floor plan style:
  // Either the is_custom flag is set, or any saved value differs from defaults
  const isCustomized = (() => {
    if (!floorPlanStyle || Object.keys(floorPlanStyle).length === 0) return false;
    if (floorPlanStyle.is_custom === true) return true;
    // Check if any color value differs from defaults
    for (const key of Object.keys(DEFAULTS)) {
      if (floorPlanStyle[key] !== undefined && floorPlanStyle[key] !== DEFAULTS[key]) {
        return true;
      }
    }
    return false;
  })();

  // Merge: theme wins by default; user's explicit customization wins over theme
  const styles = isCustomized
    ? { ...DEFAULTS, ...themeDefaults, ...floorPlanStyle }
    : { ...DEFAULTS, ...floorPlanStyle, ...themeDefaults };
  
  const placedTables = React.useMemo(
    () => tables.filter(t => t.position_x != null && t.position_y != null && t.shape !== "grouped"),
    [tables]
  );

  const groupedTables = React.useMemo(
    () => tables.filter(t => t.shape === "grouped" && t.grouped_piece_ids?.length > 0),
    [tables]
  );

  const placedVenueElements = React.useMemo(
    () => venueElements.filter(el => el.position_x != null && el.position_y != null),
    [venueElements]
  );

  const placedCustomPieces = React.useMemo(
    () => customPieces.filter(p => p.position_x != null && p.position_y != null),
    [customPieces]
  );
  
  const hasContent =
    placedTables.length > 0 ||
    placedVenueElements.length > 0 ||
    groupedTables.length > 0 ||
    placedCustomPieces.length > 0;

  const getViewBounds = () => {
    if (!hasContent) {
      let maxWidth, maxHeight;
      if (fullScreen) {
        maxWidth = containerSize?.width || 400;
        maxHeight = containerSize?.height || 300;
      } else {
        maxWidth = compact ? 300 : 400;
        maxHeight = compact ? 200 : 300;
      }

      return {
        minX: 0,
        minY: 0,
        width: 1,
        height: 1,
        scale: 1,
        offsetX: 0,
        offsetY: 0,
        maxWidth,
        maxHeight,
      };
    }

    // Hybrid bounds constants
    const TABLE_MARGIN = 40;
    const IMPORTANT_MARGIN = 20;
    const FINAL_MARGIN = 10;

    const IMPORTANT_TYPES = new Set(["stage", "dance_floor", "bar", "entrance", "dj_booth", "photo_booth", "restrooms", "gift_table", "cake_table", "buffet", "seating_area"]);

    // ✅ STEP 1: Tables bounds
    let tMinX = Infinity, tMinY = Infinity, tMaxX = -Infinity, tMaxY = -Infinity;
    
    placedTables.forEach((t) => {
      const cfg = SHAPE_CONFIGS[t.shape || "round"] || SHAPE_CONFIGS.round;
      const w = t.width || cfg.width;
      const h = t.height || cfg.height;

      // seats exist for ALL shapes
      const seatPad = getSeatPad(t.shape);
      const outer = seatPad + STROKE_PAD;

      tMinX = Math.min(tMinX, t.position_x - outer);
      tMinY = Math.min(tMinY, t.position_y - outer);
      tMaxX = Math.max(tMaxX, t.position_x + w + outer);
      tMaxY = Math.max(tMaxY, t.position_y + h + outer);
    });

    // Fallback if no tables + apply table margin
    if (!isFinite(tMinX)) { tMinX = 0; tMinY = 0; tMaxX = 1; tMaxY = 1; }
    tMinX -= TABLE_MARGIN; tMinY -= TABLE_MARGIN;
    tMaxX += TABLE_MARGIN; tMaxY += TABLE_MARGIN;

    // ✅ Include custom pieces in bounds calculation
    placedCustomPieces.forEach((p) => {
      const w = p.width || 60;
      const h = p.height || 60;
      const pad = 10;
      
      tMinX = Math.min(tMinX, p.position_x - pad);
      tMinY = Math.min(tMinY, p.position_y - pad);
      tMaxX = Math.max(tMaxX, p.position_x + w + pad);
      tMaxY = Math.max(tMaxY, p.position_y + h + pad);
    });

    // ✅ STEP 2: ALL venue elements contribute to bounds
    let vMinX = Infinity, vMinY = Infinity, vMaxX = -Infinity, vMaxY = -Infinity;
    let hasVenueElements = false;

    const outerPad = 2;

    // Include ALL venue elements in bounds (not just "important" types)
    placedVenueElements.forEach((el) => {
      const cfg = VENUE_ELEMENT_CONFIGS[el.element_type] || VENUE_ELEMENT_CONFIGS.stage;
      const w = el.width || cfg.defaultWidth;
      const h = el.height || cfg.defaultHeight;
      const rotPad = el.rotation ? Math.max(w, h) * 0.25 : 0;

      const x0 = el.position_x - outerPad - rotPad - IMPORTANT_MARGIN;
      const y0 = el.position_y - outerPad - rotPad - IMPORTANT_MARGIN;
      const x1 = el.position_x + w + outerPad + rotPad + IMPORTANT_MARGIN;
      const y1 = el.position_y + h + outerPad + rotPad + IMPORTANT_MARGIN;

      vMinX = Math.min(vMinX, x0);
      vMinY = Math.min(vMinY, y0);
      vMaxX = Math.max(vMaxX, x1);
      vMaxY = Math.max(vMaxY, y1);
      hasVenueElements = true;
    });

    // Include text boxes in bounds (they expand like venue elements)
    textBoxes.forEach(tb => {
      if (tb.position_x == null || tb.position_y == null) return;
      const w = tb.width || 150;
      const h = tb.height || 40;
      const rotPad = tb.rotation ? Math.max(w, h) * 0.25 : 0;
      
      const x0 = tb.position_x - rotPad - 20;
      const y0 = tb.position_y - rotPad - 20;
      const x1 = tb.position_x + w + rotPad + 20;
      const y1 = tb.position_y + h + rotPad + 20;

      if (!hasVenueElements) {
        // If no tables/elements, use text boxes for bounds
        tMinX = Math.min(tMinX, x0);
        tMinY = Math.min(tMinY, y0);
        tMaxX = Math.max(tMaxX, x1);
        tMaxY = Math.max(tMaxY, y1);
      } else {
        vMinX = Math.min(vMinX, x0);
        vMinY = Math.min(vMinY, y0);
        vMaxX = Math.max(vMaxX, x1);
        vMaxY = Math.max(vMaxY, y1);
      }
    });

    // ✅ STEP 3: Final bounds - include ALL elements without limits
    let minX = tMinX, minY = tMinY, maxX = tMaxX, maxY = tMaxY;

    if (hasVenueElements) {
      // Include ALL venue elements - no MAX_EXTRA clamping for public view
      minX = Math.min(tMinX, vMinX);
      minY = Math.min(tMinY, vMinY);
      maxX = Math.max(tMaxX, vMaxX);
      maxY = Math.max(tMaxY, vMaxY);
    }

    // Final small cushion
    minX -= FINAL_MARGIN; minY -= FINAL_MARGIN;
    maxX += FINAL_MARGIN; maxY += FINAL_MARGIN;
    
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    
    // Step 3: Container size with fallbacks
    let maxWidth, maxHeight;
    if (fullScreen) {
      maxWidth = containerSize?.width || 400;
      maxHeight = containerSize?.height || 300;
    } else {
      maxWidth = compact ? 300 : 400;
      maxHeight = compact ? 200 : 300;
    }
    
    // Step 1: Guard against 0/NaN scale
    const safeMaxWidth = Math.max(1, maxWidth || 0);
    const safeMaxHeight = Math.max(1, maxHeight || 0);
    const safeContentWidth = Math.max(1, contentWidth || 0);
    const safeContentHeight = Math.max(1, contentHeight || 0);

    const widthScale = safeMaxWidth / safeContentWidth;
    const heightScale = safeMaxHeight / safeContentHeight;
    let scale = Math.min(widthScale, heightScale);
    
    // Small fit margin to prevent horizontal clipping
    const isLandscapeViewport = safeMaxWidth > safeMaxHeight;
    const FIT_MARGIN_LANDSCAPE = 0.975;

    if (isLandscapeViewport) {
      scale *= FIT_MARGIN_LANDSCAPE;
    }
    
    // MINIMUM SCALE: Prevent content from getting too small when there are many elements
    // This ensures table names stay readable. If scale would be below this, we allow panning instead.
    const MIN_READABLE_SCALE = 0.4;
    const needsPan = scale < MIN_READABLE_SCALE;
    if (needsPan) {
      scale = MIN_READABLE_SCALE;
    }
    
    // Safety clamp
    if (!Number.isFinite(scale) || scale <= 0) scale = 1;
    scale = Math.min(scale, 2.5);
    
    // Scaled content dimensions
    const scaledW = contentWidth * scale;
    const scaledH = contentHeight * scale;
    
    // Centering offsets
    // IMPORTANT: allow negative offsets when content is larger than viewport (so zoomed layouts stay centered)
    const offsetX = (maxWidth - scaledW) / 2;
    const offsetY = (maxHeight - scaledH) / 2;
    
    return { minX, minY, width: contentWidth, height: contentHeight, scale, offsetX, offsetY, maxWidth, maxHeight };
  };

  const bounds = React.useMemo(() => getViewBounds(), [
    placedTables,
    placedVenueElements,
    placedCustomPieces,
    compact,
    fullScreen,
    containerSize?.width,
    containerSize?.height,
  ]);
  const primaryColor = themeColors.primary || '#D4AF37';
  const backgroundColor = themeColors.background || '#0f172a';

  // View state + refs (pan/zoom)
  const containerRef = React.useRef(null);
  const [view, setView] = React.useState({ x: 0, y: 0, z: 1 });

  const viewRef = React.useRef(view);
  React.useEffect(() => {
    viewRef.current = view;
  }, [view]);

  const pointersRef = React.useRef(new Map());
  const gestureRef = React.useRef({
    mode: null,
    startX: 0,
    startY: 0,
    startViewX: 0,
    startViewY: 0,
    startDist: 0,
    startZ: 1,
    // pinch anchor data
    startTx: 0,
    startTy: 0,
    startFinalScale: 1,
  });

  const tapRef = React.useRef({ t: 0 });

  const defaultZRef = React.useRef(1);

  const isInteractingRef = React.useRef(false);

  const clamp = React.useCallback((v, min, max) => Math.max(min, Math.min(max, v)), []);
  const dist2 = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const mid2 = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  // Compute defaultZ ONCE per render
  // For portrait mode, use higher zoom since wide content shrinks a lot to fit narrow screen
  const isPortraitViewport =
    (bounds.maxHeight / Math.max(1, bounds.maxWidth)) > 1.15;
  
  // Calculate content aspect ratio to determine zoom boost
  const contentAspectRatio = bounds.width / Math.max(1, bounds.height);
  const isWideContent = contentAspectRatio > 1.3; // Content is wider than tall
  
  // Portrait + wide content needs more zoom
  let defaultZ = 1;
  if (isPortraitViewport) {
    defaultZ = isWideContent ? 1.45 : 1.30; // Adjusted zoom for wide floor plans in portrait
  }

  // Zoom constants (must NOT depend on defaultZ)
  const MIN_Z = 0.75;
  const MAX_Z = 3.5;

  const finalScale = bounds.scale * view.z;

  // Clamp helper for pan limits (correct for single-stage transform)
  const clampView = React.useCallback((v) => {
    const vw = bounds.maxWidth;
    const vh = bounds.maxHeight;

    const s = bounds.scale * v.z;
    const scaledW = bounds.width * s;
    const scaledH = bounds.height * s;

    let x = v.x;
    let y = v.y;

    // Calculate center positions
    const centerX = Math.round((vw - scaledW) / 2);
    const centerY = Math.round((vh - scaledH) / 2);

    if (scaledW <= vw) {
      // Content fits horizontally - always center
      x = centerX;
    } else {
      // Content overflows horizontally - allow panning
      // On initial load (x=0), start centered
      if (v.x === 0 && !isInteractingRef.current) {
        x = centerX;
      } else {
        // Clamp to valid pan range only when user is panning
        x = clamp(v.x, vw - scaledW, 0);
      }
    }

    if (scaledH <= vh) {
      // Content fits vertically - always center
      y = centerY;
    } else {
      // Content overflows vertically - allow panning
      // On initial load (y=0), start centered vertically too
      if (v.y === 0 && !isInteractingRef.current) {
        y = centerY;
      } else {
        // Clamp to valid pan range (negative values show content below)
        y = clamp(v.y, vh - scaledH, 0);
      }
    }

    return { ...v, x, y };
  }, [bounds.maxWidth, bounds.maxHeight, bounds.scale, bounds.width, bounds.height, clamp]);

  // Reset view when camera base changes
  React.useEffect(() => {
    if (isInteractingRef.current) return;

    defaultZRef.current = defaultZ;
    setView(() => clampView({ x: 0, y: 0, z: defaultZ }));
  }, [
    defaultZ,
    bounds.scale,
    bounds.width,
    bounds.height,
    bounds.maxWidth,
    bounds.maxHeight,
    clampView,
  ]);

  // Convert client coords to local container coords
  const toLocal = (e) => {
    const r = containerRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  // Pointer handlers
  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    isInteractingRef.current = true;

    const now = Date.now();

    const isFirstPointer = pointersRef.current.size === 0;

    if (isFirstPointer && now - tapRef.current.t < 280) {
      setView(() => clampView({ x: 0, y: 0, z: defaultZRef.current }));
      tapRef.current.t = 0;
      return;
    }

    if (isFirstPointer) tapRef.current.t = now;

    pointersRef.current.set(e.pointerId, toLocal(e));

    const pts = Array.from(pointersRef.current.values());

    if (pts.length === 1) {
      gestureRef.current.mode = "drag";
      const local = toLocal(e);
      gestureRef.current.startX = local.x;
      gestureRef.current.startY = local.y;
      gestureRef.current.startViewX = view.x;
      gestureRef.current.startViewY = view.y;
    }

    if (pts.length === 2) {
      gestureRef.current.mode = "pinch";
      const [p1, p2] = pts;

      const cx = (p1.x + p2.x) / 2;
      const cy = (p1.y + p2.y) / 2;

      gestureRef.current.startDist = dist2(p1, p2);
      gestureRef.current.startZ = view.z;

      const s0 = bounds.scale * view.z;
      gestureRef.current.startWorld = {
        x: (cx - view.x) / s0,
        y: (cy - view.y) / s0,
      };
    }
  };

  const onPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, toLocal(e));

    const pts = Array.from(pointersRef.current.values());
    const mode = gestureRef.current.mode;

    // Handle drag OR fallback from pinch with only 1 pointer remaining
    if ((mode === "drag" || mode === "pinch") && pts.length === 1) {
      if (mode === "pinch") {
        // reinit drag baseline once
        gestureRef.current.mode = "drag";
        gestureRef.current.startX = pts[0].x;
        gestureRef.current.startY = pts[0].y;
        const v = viewRef.current;
        gestureRef.current.startViewX = v.x;
        gestureRef.current.startViewY = v.y;
      }

      e.preventDefault();
      const local = pts[0];
      const dx = local.x - gestureRef.current.startX;
      const dy = local.y - gestureRef.current.startY;

      setView(prev => clampView({
        ...prev,
        x: gestureRef.current.startViewX + dx,
        y: gestureRef.current.startViewY + dy,
      }));
      return;
    }

    if (mode === "pinch" && pts.length === 2) {
      e.preventDefault();

      const [p1, p2] = pts;

      const cx = (p1.x + p2.x) / 2;
      const cy = (p1.y + p2.y) / 2;

      const d = dist2(p1, p2);
      const ratio = d / Math.max(1, gestureRef.current.startDist);

      let nextZ = gestureRef.current.startZ * ratio;
      nextZ = clamp(nextZ, MIN_Z, MAX_Z);

      const s1 = bounds.scale * nextZ;
      const w = gestureRef.current.startWorld;

      const x = cx - w.x * s1;
      const y = cy - w.y * s1;

      setView((prev) => clampView({ ...prev, x, y, z: nextZ }));
    }
  };

  const reinitDragFromLastPointer = () => {
    gestureRef.current.mode = "drag";
    const [lastPointer] = Array.from(pointersRef.current.values());
    const v = viewRef.current;

    gestureRef.current.startX = lastPointer.x;
    gestureRef.current.startY = lastPointer.y;
    gestureRef.current.startViewX = v.x;
    gestureRef.current.startViewY = v.y;
  };

  const endInteractionIfNoneLeft = () => {
    if (pointersRef.current.size === 0) {
      gestureRef.current.mode = null;
      isInteractingRef.current = false;
      setView((prev) => clampView(prev));
    }
  };

  const onPointerUp = (e) => {
    // optional, but good hygiene
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}

    pointersRef.current.delete(e.pointerId);

    // pinch -> drag transition
    if (pointersRef.current.size === 1 && gestureRef.current.mode === "pinch") {
      reinitDragFromLastPointer();
      return;
    }

    endInteractionIfNoneLeft();
  };

  const onPointerCancel = (e) => {
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}

    pointersRef.current.delete(e.pointerId);

    if (pointersRef.current.size === 1 && gestureRef.current.mode === "pinch") {
      reinitDragFromLastPointer();
      return;
    }

    endInteractionIfNoneLeft();
  };

  if (!hasContent) return null;

  // Full screen mode
  if (fullScreen) {
    return (
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
          touchAction: 'none',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: bounds.width,
            height: bounds.height,
            transform: `translate(${view.x}px, ${view.y}px) scale(${finalScale})`,
            transformOrigin: '0 0',
            willChange: 'transform',
          }}
        >
          {placedTables.map(table => {
            const config = SHAPE_CONFIGS[table.shape || 'round'] || SHAPE_CONFIGS.round;
            const isHighlighted = highlightedTableId === table.id;
            const tableWidth = table.width || config.width;
            const tableHeight = table.height || config.height;
            const needsCustomSvg = config.isCustomSvg;
            // Use custom styles for colors
            const strokeColor = isHighlighted ? styles.highlighted_table_color : styles.table_stroke_color;
            const fillColor = isHighlighted ? `${styles.highlighted_table_color}66` : styles.table_fill_color;
            
            const seats = table.seats || 8;
            const seatPositions = calculateSeatPositions(table.shape || 'round', tableWidth, tableHeight, seats);
            
            const seatPad = getSeatPad(table.shape);
            const outer = seatPad + STROKE_PAD;
            
            // Use ORIGINAL coordinates - scale applied via CSS transform on parent
            return (
              <div key={table.id} className={`absolute transition-all duration-300 ${isHighlighted ? 'z-10' : 'z-0'}`}
                style={{
                  left: table.position_x - bounds.minX - outer,
                  top: table.position_y - bounds.minY - outer,
                  width: tableWidth + outer * 2,
                  height: tableHeight + outer * 2,
                  transform: table.rotation ? `rotate(${table.rotation}deg)` : 'none',
                }}>
                <div className="absolute flex items-center justify-center"
                  style={{
                    left: outer,
                    top: outer,
                    width: tableWidth,
                    height: tableHeight,
                    borderRadius: needsCustomSvg ? '0' : config.borderRadius,
                    backgroundColor: needsCustomSvg ? 'transparent' : fillColor,
                    border: needsCustomSvg ? 'none' : `${styles.table_stroke_width}px solid ${strokeColor}`,
                    boxShadow: isHighlighted ? `0 0 25px ${styles.highlighted_table_color}66` : 'none',
                  }}>
                  {needsCustomSvg && renderTableSVG(table.shape, tableWidth, tableHeight, strokeColor, fillColor)}
                  <span className="font-semibold px-1 absolute z-10 whitespace-nowrap text-center" style={{ 
                    color: isHighlighted ? (styles.highlighted_font_color || '#fff') : (styles.table_name_font_color || `${primaryColor}cc`), 
                    left: '50%',
                    top: '50%',
                    transform: `translate(-50%, -50%) translate(${table.label_offset_x || 0}px, ${table.label_offset_y || 0}px)`,
                    fontSize: isHighlighted 
                      ? `${styles.highlighted_font_size || 16}px` 
                      : (!needsCustomSvg ? `${Math.max(styles.table_name_font_size - 2, Math.min(styles.table_name_font_size + 6, Math.min(tableWidth, tableHeight) * 0.18))}px` : `${styles.table_name_font_size}px`)
                  }}>
                    {table.table_name}
                  </span>
                </div>
                {seatPositions.map((pos, idx) => (
                  <div key={idx} className="absolute"
                    style={{
                      left: pos.x + outer,
                      top: pos.y + outer,
                      width: SEAT_DIAMETER,
                      height: SEAT_DIAMETER,
                      borderRadius: '50%',
                      backgroundColor: `${styles.seat_color}66`,
                      border: `1px solid ${styles.seat_color}`,
                    }}
                  />
                ))}
              </div>
            );
          })}
          {/* Venue elements - original coordinates */}
          {placedVenueElements.map(el => {
            const config = VENUE_ELEMENT_CONFIGS[el.element_type] || VENUE_ELEMENT_CONFIGS.stage;
            const w = el.width || config.defaultWidth;
            const h = el.height || config.defaultHeight;
            return (
              <div key={el.id} className="absolute flex items-center justify-center"
                style={{
                  left: el.position_x - bounds.minX,
                  top: el.position_y - bounds.minY,
                  width: w,
                  height: h,
                  backgroundColor: styles.venue_element_color,
                  border: `1px dashed ${styles.venue_element_stroke}`,
                  borderRadius: '4px',
                  transform: el.rotation ? `rotate(${el.rotation}deg)` : 'none',
                }}>
                <span style={{ fontSize: `${styles.venue_element_font_size || 12}px`, color: styles.venue_element_font_color || styles.venue_element_stroke }}>{el.label || config.label}</span>
              </div>
            );
          })}
          
          {/* Text Boxes */}
          {textBoxes.map(tb => (
            <div key={tb.id} className="absolute"
              style={{
                left: tb.position_x - bounds.minX,
                top: tb.position_y - bounds.minY,
                width: tb.width || 150,
                height: tb.height || 40,
                transform: tb.rotation ? `rotate(${tb.rotation}deg)` : 'none',
                backgroundColor: tb.background_color || 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: tb.text_align === 'left' ? 'flex-start' : tb.text_align === 'right' ? 'flex-end' : 'center',
                padding: '4px 8px',
              }}>
              <span style={{
                fontSize: `${tb.font_size || 16}px`,
                color: tb.font_color || '#000000',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {tb.text}
              </span>
            </div>
          ))}
          
          {/* Custom Pieces */}
          {placedCustomPieces.map(piece => {
            const isHighlighted = false; // Custom pieces don't highlight
            return (
              <div key={piece.id} className="absolute"
                style={{
                  left: piece.position_x - bounds.minX,
                  top: piece.position_y - bounds.minY,
                  width: piece.width || 60,
                  height: piece.height || 60,
                  transform: piece.rotation ? `rotate(${piece.rotation}deg)` : 'none',
                }}>
                <CustomPieceShape piece={piece} scale={1} primaryColor={primaryColor} isHighlighted={isHighlighted} styles={styles}/>
                {/* Render seats for custom piece */}
                {piece.seats > 0 && piece.seat_offsets && Object.entries(piece.seat_offsets).slice(0, piece.seats).map(([idx, offset]) => (
                  <div
                    key={`seat-${idx}`}
                    className="absolute"
                    style={{
                      left: offset.x,
                      top: offset.y,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: `${styles.seat_color}66`,
                      border: `1px solid ${styles.seat_color}`,
                    }}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Normal mode with container
  return (
    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: `${backgroundColor}22`, border: `1px solid ${primaryColor}33` }}>
      <div className="px-4 py-2 flex items-center gap-2" style={{ backgroundColor: `${primaryColor}11`, borderBottom: `1px solid ${primaryColor}33` }}>
        <MapPin className="h-4 w-4" style={{ color: primaryColor }} />
        <span className="text-sm font-medium" style={{ color: primaryColor }}>Floor Plan</span>
      </div>
      
      <div className="p-3">
        <div className="relative mx-auto" style={{ width: bounds.width * bounds.scale, height: bounds.height * bounds.scale }}>
          {placedTables.map(table => {
            const config = SHAPE_CONFIGS[table.shape || 'round'] || SHAPE_CONFIGS.round;
            const isHighlighted = highlightedTableId === table.id;
            const tableWidth = table.width || config.width;
            const tableHeight = table.height || config.height;
            const needsCustomSvg = config.isCustomSvg;
            // Use custom styles
            const strokeColor = isHighlighted ? styles.highlighted_table_color : styles.table_stroke_color;
            const fillColor = isHighlighted ? `${styles.highlighted_table_color}66` : styles.table_fill_color;
            
            // Calculate seat positions
            const seats = table.seats || 8;
            const seatPositions = calculateSeatPositions(table.shape || 'round', tableWidth, tableHeight, seats);
            
            // For round/circle tables, add extra padding for seats
            const isRoundShape = table.shape === 'round' || table.shape === 'circle' || !table.shape;
            const seatPadding = isRoundShape ? 10 : 0; // Reduced padding
            const containerWidth = tableWidth + (seatPadding * 2);
            const containerHeight = tableHeight + (seatPadding * 2);
            const scaledWidth = containerWidth * bounds.scale;
            const scaledHeight = containerHeight * bounds.scale;
            
            return (
              <div key={table.id} className={`absolute transition-all duration-300 ${isHighlighted ? 'z-10' : 'z-0'}`}
                style={{
                  left: (table.position_x - bounds.minX - seatPadding) * bounds.scale,
                  top: (table.position_y - bounds.minY - seatPadding) * bounds.scale,
                  width: scaledWidth,
                  height: scaledHeight,
                  transform: table.rotation ? `rotate(${table.rotation}deg)` : 'none',
                }}>
                {/* Table shape */}
                <div className="absolute flex items-center justify-center"
                  style={{
                    left: seatPadding * bounds.scale,
                    top: seatPadding * bounds.scale,
                    width: tableWidth * bounds.scale,
                    height: tableHeight * bounds.scale,
                    borderRadius: needsCustomSvg ? '0' : config.borderRadius,
                    backgroundColor: needsCustomSvg ? 'transparent' : fillColor,
                    border: needsCustomSvg ? 'none' : `${styles.table_stroke_width}px solid ${strokeColor}`,
                    boxShadow: isHighlighted ? `0 0 15px ${styles.highlighted_table_color}55` : 'none',
                  }}>
                  {needsCustomSvg && renderTableSVG(table.shape, tableWidth, tableHeight, strokeColor, fillColor)}
                  <span className="font-semibold px-1 absolute z-10 whitespace-nowrap text-center" style={{ 
                    color: isHighlighted ? (styles.highlighted_font_color || '#fff') : (styles.table_name_font_color || `${primaryColor}cc`), 
                    left: '50%',
                    top: '50%',
                    transform: `translate(-50%, -50%) translate(${(table.label_offset_x || 0) * bounds.scale}px, ${(table.label_offset_y || 0) * bounds.scale}px)`,
                    fontSize: isHighlighted 
                      ? `${(styles.highlighted_font_size || 16) * bounds.scale}px` 
                      : (!needsCustomSvg ? `${Math.max(styles.table_name_font_size - 4, Math.min(styles.table_name_font_size + 2, Math.min(tableWidth, tableHeight) * bounds.scale * 0.15))}px` : `${styles.table_name_font_size - 2}px`)
                  }}>
                    {table.table_name}
                  </span>
                </div>
                {/* Seats - visual only for all tables */}
                {seatPositions.map((pos, idx) => (
                  <div key={idx} className="absolute"
                    style={{
                      left: (pos.x + seatPadding) * bounds.scale,
                      top: (pos.y + seatPadding) * bounds.scale,
                      width: 6 * bounds.scale,
                      height: 6 * bounds.scale,
                      borderRadius: '50%',
                      backgroundColor: `${styles.seat_color}66`,
                      border: `1px solid ${styles.seat_color}`,
                    }}
                  />
                ))}
              </div>
            );
          })}

          {placedCustomPieces.map(piece => {
            const config = PIECE_CONFIGS[piece.piece_type] || PIECE_CONFIGS.piece_rectangle;
            const parentGroupedTable = groupedTables.find(t => t.grouped_piece_ids?.includes(piece.id));
            const isHighlighted = parentGroupedTable?.id === highlightedTableId;
            return (
              <div key={piece.id} className={`absolute ${isHighlighted ? 'z-10' : 'z-0'}`}
                style={{ left: (piece.position_x - bounds.minX) * bounds.scale, top: (piece.position_y - bounds.minY) * bounds.scale, width: (piece.width || config.width) * bounds.scale, height: (piece.height || config.height) * bounds.scale }}>
                <CustomPieceShape piece={piece} scale={bounds.scale} primaryColor={primaryColor} isHighlighted={isHighlighted} styles={styles}/>
              </div>
            );
          })}

          {placedVenueElements.map(element => {
            const config = VENUE_ELEMENT_CONFIGS[element.element_type] || VENUE_ELEMENT_CONFIGS.stage;
            const elWidth = element.width || config.defaultWidth;
            const elHeight = element.height || config.defaultHeight;
            return (
              <div key={element.id} className="absolute flex items-center justify-center"
                style={{ left: (element.position_x - bounds.minX) * bounds.scale, top: (element.position_y - bounds.minY) * bounds.scale, width: elWidth * bounds.scale, height: elHeight * bounds.scale, borderRadius: config.isCircle ? '50%' : '2px', backgroundColor: styles.venue_element_color, border: `1px dashed ${styles.venue_element_stroke}`, transform: element.rotation ? `rotate(${element.rotation}deg)` : 'none' }}>
                <span className="font-medium text-center px-1 select-none" style={{ fontSize: `${Math.max(8, (styles.venue_element_font_size || 12) * bounds.scale)}px`, color: styles.venue_element_font_color || styles.venue_element_stroke }}>{element.label || config.label}</span>
              </div>
            );
          })}
          
          {/* Text Boxes in compact mode */}
          {textBoxes.map(tb => (
            <div key={tb.id} className="absolute"
              style={{
                left: (tb.position_x - bounds.minX) * bounds.scale,
                top: (tb.position_y - bounds.minY) * bounds.scale,
                width: (tb.width || 150) * bounds.scale,
                height: (tb.height || 40) * bounds.scale,
                transform: tb.rotation ? `rotate(${tb.rotation}deg)` : 'none',
                backgroundColor: tb.background_color || 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: tb.text_align === 'left' ? 'flex-start' : tb.text_align === 'right' ? 'flex-end' : 'center',
                padding: `${2 * bounds.scale}px ${4 * bounds.scale}px`,
              }}>
              <span style={{
                fontSize: `${(tb.font_size || 16) * bounds.scale}px`,
                color: tb.font_color || '#000000',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {tb.text}
              </span>
            </div>
          ))}
        </div>
      </div>
      
      {highlightedTableId && (
        <div className="px-4 py-2 text-center" style={{ backgroundColor: `${primaryColor}11`, borderTop: `1px solid ${primaryColor}33` }}>
          <span className="text-xs" style={{ color: `${primaryColor}99` }}>Your table is highlighted above</span>
        </div>
      )}
    </div>
  );
}
